import { toast } from "sonner";
import {
  isBackendUnavailableMessage,
  useBackendHealthStore,
} from "@/stores/useBackendHealthStore";
import {
  sanitizeOptionalUserFacingErrorMessage,
  sanitizeUserFacingErrorMessage,
} from "@/lib/error/user-facing-error";

interface ToastOptions {
  title: string;
  description?: string | string[];
  duration?: number;
}

function formatDescription(
  desc: string | string[] | undefined,
  sanitize = false,
): string | undefined {
  if (!desc) return undefined;
  if (Array.isArray(desc)) {
    const lines = sanitize
      ? desc.map(sanitizeOptionalUserFacingErrorMessage).filter(Boolean)
      : desc;
    return lines.length ? lines.join("\n") : undefined;
  }
  return sanitize ? sanitizeOptionalUserFacingErrorMessage(desc) : desc;
}

function suppressBackendUnavailableToast(title: string, description?: string): boolean {
  if (
    !isBackendUnavailableMessage(title) &&
    !isBackendUnavailableMessage(description)
  ) {
    return false;
  }
  useBackendHealthStore.getState().reportBackendFailure(title);
  return true;
}

export const showToast = {
  success: ({ title, description, duration = 4000 }: ToastOptions) => {
    toast.success(title, { description: formatDescription(description), duration });
  },
  // Error toasts auto-dismiss after 6s — slightly longer than success (4s) so
  // the user has time to read but not so long they linger on the screen.
  // Pass duration: Infinity for cases that genuinely require acknowledgement
  // (e.g. destructive failures the user must investigate before retrying).
  error: ({ title, description, duration = 6000 }: ToastOptions) => {
    const cleanTitle = sanitizeUserFacingErrorMessage(title);
    const cleanDescription = formatDescription(description, true);
    if (suppressBackendUnavailableToast(cleanTitle, cleanDescription)) return;
    toast.error(cleanTitle, { description: cleanDescription, duration });
  },
  warning: ({ title, description, duration = 4000 }: ToastOptions) => {
    toast.warning(title, { description: formatDescription(description), duration });
  },
  info: ({ title, description, duration = 4000 }: ToastOptions) => {
    toast.info(title, { description: formatDescription(description), duration });
  },
};

export const showSuccess = (message: string) => showToast.success({ title: message });
export const showError = (message: string) => showToast.error({ title: message });
export const showInfo = (message: string) => showToast.info({ title: message });
export const showWarning = (message: string) => showToast.warning({ title: message });
